import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { handleSetShareModalStatus } from "../../../fileManager.actions";
import Button from "../../../../button/button.index";
import Dialog from "../../../../dialog/dialog.index";
import { DialogSize } from "../../../../dialog/dialog.interface";
import { ShareModalTabStatus } from "./shareModal.interface";
import { ResourceType, UserType } from "../../../../../../interfaces/general";
import { advancedResponseValidator, getAll } from "../../../../../../scripts/api";
import API from "../../../../../../urls/api";
import { getObject } from "../../../fileManager.function";
import Loader from "../../../../loader/loader.index";
import { addZeroToStart, isFolder } from "../../../../../../scripts/helpers";
import { useAppSelector } from "../../../../../../redux/hooks";
import { shallowEqual } from "react-redux";
import { Radio } from "../../../../radio/radio.index";

const ShareWithContacts = lazy(() => import("./shareWithContancts/shareWithContacts.index").then((share) => ({ default: share.ShareWithContacts })));
const ShareWithPublic = lazy(() => import("./shareWithPublic/shareWithPublic.index").then((share) => ({ default: share.ShareWithPublic })));

export default function ShareModal(): JSX.Element {
    const { shareModalStatus, informationModalStatus, selectedResources } = useAppSelector(
        ({ fileManager: { shareModalStatus, informationModalStatus, selectedResources } }) => ({
            shareModalStatus,
            informationModalStatus,
            selectedResources
        }),
        shallowEqual
    );
    const [tab, setTab] = useState<ShareModalTabStatus>(ShareModalTabStatus.SHARE_WITH_CONTACTS);
    const [users, setUsers] = useState<UserType[] | undefined>(undefined);
    const [selectedResource, setSelectedResource] = useState<ResourceType | undefined>(undefined);
    const [tempUser, setTempUser] = useState<{ name: string; phone: string }>();
    const shareWithContactsRef = useRef<any>(null);
    const shareWithPublicRef = useRef<any>(null);
    const shareButtonRef = useRef<any>();
    const hiddenInputRef = useRef<any>();
    const [organizationId, setOrganizationId] = useState<number | null>(null);

    useEffect(() => {
        if (hiddenInputRef.current) hiddenInputRef.current.focus();
    });

    useEffect(() => {
        if (!shareModalStatus) {
            setTab(ShareModalTabStatus.SHARE_WITH_CONTACTS);
            if (!informationModalStatus) {
                setSelectedResource(undefined);
            }
            setUsers(undefined);
            setTempUser({ name: "", phone: "" });
            setOrganizationId(null);
        }
    }, [shareModalStatus]);

    useEffect(() => {
        if (shareModalStatus) {
            const params = organizationId ? { org_id: organizationId } : {};
            getAll(
                (data, status) => {
                    if (advancedResponseValidator(data, status)) {
                        setUsers(data.result.map((contact: any) => ({ ...contact, phone: addZeroToStart(contact.phone) })));
                    }
                },
                API.contact.list(),
                params
            );
            if (selectedResources[0]) {
                getObject(selectedResources[0].id).then((data) => setSelectedResource(data));
            }
        }
    }, [shareModalStatus, organizationId]);

    const onShareModalClose = () => {
        handleSetShareModalStatus(false);
    };

    const handleSubmit = () => {
        return new Promise((resolve) => {
            if (tab === ShareModalTabStatus.SHARE_WITH_CONTACTS) {
                shareWithContactsRef.current.submit().then(() => resolve(true));
            } else {
                shareWithPublicRef.current.submit().then(() => resolve(true));
            }
        });
    };

    return (
        <Dialog
            status={shareModalStatus}
            onClose={onShareModalClose}
            title="اشتراک‌گذاری"
            closeButton={true}
            size={DialogSize.MD}
            className={`min-h-[600px]`}
        >
            {users && selectedResource ? (
                <div className="flex flex-col h-full">
                    {isFolder(selectedResource) && (
                        <div className="flex mx-6 mt-4">
                            <Radio
                                value={tab}
                                options={[
                                    { title: "اشتراک گذاری با مخاطبین", payload: ShareModalTabStatus.SHARE_WITH_CONTACTS },
                                    { title: "اشتراک گذاری عمومی", payload: ShareModalTabStatus.SHARE_WITH_PUBLIC }
                                ]}
                                onChange={(value) => setTab(value)}
                            />
                        </div>
                    )}

                    <div
                        className="flex-1 overflow-auto"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                shareButtonRef.current.click();
                            } else if (e.key === "Escape") {
                                onShareModalClose();
                            }
                        }}
                    >
                        <button className="w-0 h-0 absolute" ref={hiddenInputRef} />
                        {tab === ShareModalTabStatus.SHARE_WITH_CONTACTS ? (
                            <Suspense>
                                {" "}
                                <ShareWithContacts
                                    inputRef={hiddenInputRef}
                                    ref={shareWithContactsRef}
                                    resource={selectedResource}
                                    users={users}
                                    onChange={(user: { name: string; phone: string }) => setTempUser(user)}
                                    tempUser={tempUser}
                                    orgId={organizationId}
                                    onOrganizationChange={setOrganizationId}
                                />
                            </Suspense>
                        ) : (
                            <Suspense>
                                <ShareWithPublic inputRef={hiddenInputRef} ref={shareWithPublicRef} resource={selectedResource} />
                            </Suspense>
                        )}
                    </div>
                    <div className="p-4">
                        <Button
                            text={`${tab === ShareModalTabStatus.SHARE_WITH_CONTACTS ? "اشتراک‌گذاری" : "ایجاد و کپی"}`}
                            className="w-full"
                            onClick={handleSubmit}
                            autoFocus={true}
                            ref={shareButtonRef}
                        />
                    </div>
                </div>
            ) : (
                <Loader />
            )}
        </Dialog>
    );
}
